import React, { useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import html2canvas from 'html2canvas';
import { BackButton, LoadingButton } from '../../components/ui/Button';
import { useToast } from '../../components/ui';

const defaultApps = [
  { id: 'paypal', name: 'PayPal', icon: 'logos:paypal', color: '#003087' },
  { id: 'stripe', name: 'Stripe', icon: 'logos:stripe', color: '#635BFF' },
  { id: 'instagram', name: 'Instagram', icon: 'skill-icons:instagram', color: '#E4405F' },
  { id: 'twitter', name: 'Twitter', icon: 'pajamas:twitter', color: '#000000' },
  { id: 'tiktok', name: 'TikTok', icon: 'logos:tiktok-icon', color: '#000000' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'skill-icons:linkedin', color: '#0A66C2' },
  { id: 'shopify', name: 'Shopify', icon: 'logos:shopify', color: '#96BF48' },
  { id: 'gmail', name: 'Gmail', icon: 'logos:google-gmail', color: '#EA4335' },
  { id: 'slack', name: 'Slack', icon: 'logos:slack-icon', color: '#4A154B' },
  { id: 'snapchat', name: 'Snapchat', icon: 'logos:snapchat', color: '#FFFC00' }
];

const wallpapers = [
  { id: 'bg1', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'bg2', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'bg3', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'bg4', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }
];

const timeOptions = [
  { value: 'now', label: 'now' },
  { value: '1m', label: '1m ago' },
  { value: '2m', label: '2m ago' },
  { value: '5m', label: '5m ago' },
  { value: '10m', label: '10m ago' },
  { value: '1h', label: '1h ago' }
];

const positionOptions = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'bottom', label: 'Bottom' }
];

function FakeNotificationGenerator() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const previewRef = useRef(null);
  const customIconRef = useRef(null);

  const [selectedApp, setSelectedApp] = useState(defaultApps[1]);
  const [customAppIcon, setCustomAppIcon] = useState(null);
  const [title, setTitle] = useState('Stripe');
  const [description, setDescription] = useState('You received a payment of $XX.XX from thomas@uneed.best');
  const [position, setPosition] = useState('bottom');
  const [notificationTime, setNotificationTime] = useState('2m');
  const [carrier, setCarrier] = useState('AT&T');
  const [selectedWallpaper, setSelectedWallpaper] = useState(wallpapers[0]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [style, setStyle] = useState('liquid');
  const [customTime, setCustomTime] = useState('18:12');
  const [customDate, setCustomDate] = useState('Monday, November 24');
  const [isExporting, setIsExporting] = useState(false);

  const handleCustomIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomAppIcon(event.target?.result);
        setSelectedApp({ id: 'custom', name: 'Custom', icon: null, color: '#666' });
      };
      reader.readAsDataURL(file);
    }
  };

  const exportAsImage = async () => {
    if (!previewRef.current) return;

    setIsExporting(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true
      });

      const link = document.createElement('a');
      link.download = `fake-notification-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const notificationTheme = {
    text: isDarkMode ? '#ffffff' : '#000000',
    textSecondary: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top':
        return { top: '100px' };
      case 'center':
        return { top: '50%', transform: 'translateX(-50%) translateY(-50%)' };
      case 'bottom':
      default:
        return { bottom: '100px' };
    }
  };

  return (
    <div className="min-h-screen p-10" style={{ background: theme.bgPrimary, color: theme.textPrimary }}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <BackButton onClick={() => navigate('/projects')}>
          返回
        </BackButton>
        <h1 className="m-0 text-[1.75rem] font-bold">
          <Icon icon="mdi:bell-ring" className="mr-3 text-[#635BFF]" />
          Fake Notification Generator
        </h1>
      </div>

      <div className="flex gap-8 max-w-[1200px] flex-wrap">
        {/* Left Panel - Settings */}
        <div
          className="flex-[1_1_500px] rounded-2xl p-6 flex flex-col gap-5"
          style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
        >
          {/* Application Selection */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
              Application
            </label>
            <div className="grid grid-cols-8 gap-2">
              {defaultApps.map((app) => (
                <button
                  key={app.id}
                  onClick={() => {
                    setSelectedApp(app);
                    setTitle(app.name);
                    setCustomAppIcon(null);
                  }}
                  className="w-[45px] h-[45px] rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200"
                  style={{
                    border: selectedApp.id === app.id ? '2px solid #635BFF' : `1px solid ${theme.borderColor}`,
                    background: theme.bgPrimary
                  }}
                >
                  <Icon icon={app.icon} className="text-[28px]" />
                </button>
              ))}
              <button
                onClick={() => customIconRef.current?.click()}
                className="w-[45px] h-[45px] rounded-xl flex items-center justify-center cursor-pointer bg-transparent"
                style={{ border: `2px dashed ${theme.borderColor}` }}
              >
                {customAppIcon ? (
                  <img src={customAppIcon} alt="custom" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <Icon icon="ph:plus-circle-duotone" className="text-2xl" style={{ color: theme.textTertiary }} />
                )}
              </button>
              <input
                ref={customIconRef}
                type="file"
                accept="image/*"
                onChange={handleCustomIconUpload}
                className="hidden"
              />
            </div>
            <p className="mt-2 text-xs" style={{ color: theme.textTertiary }}>
              Click the + button to upload your own app icon (PNG, JPG, WebP)
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification Title"
              className="w-full py-2.5 px-3 rounded-lg text-sm box-border"
              style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter notification description"
              rows={3}
              className="w-full py-2.5 px-3 rounded-lg text-sm box-border resize-none font-inherit"
              style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
            />
          </div>

          {/* Position and Time Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                Position
              </label>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full py-2.5 px-3 rounded-lg text-sm"
                style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
              >
                {positionOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                Time
              </label>
              <select
                value={notificationTime}
                onChange={(e) => setNotificationTime(e.target.value)}
                className="w-full py-2.5 px-3 rounded-lg text-sm"
                style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
              >
                {timeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Carrier */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
              Carrier
            </label>
            <input
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="Enter carrier name"
              className="w-full py-2.5 px-3 rounded-lg text-sm box-border"
              style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
            />
          </div>

          {/* Wallpaper */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
              Wallpaper
            </label>
            <div className="flex gap-3">
              {wallpapers.map((wp) => (
                <button
                  key={wp.id}
                  onClick={() => setSelectedWallpaper(wp)}
                  className="w-[60px] h-[60px] rounded-xl cursor-pointer transition-all duration-200"
                  style={{
                    background: wp.gradient,
                    border: selectedWallpaper.id === wp.id ? '2px solid #635BFF' : `1px solid ${theme.borderColor}`
                  }}
                />
              ))}
            </div>
          </div>

          {/* Theme and Style Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                Theme
              </label>
              <select
                value={isDarkMode ? 'dark' : 'light'}
                onChange={(e) => setIsDarkMode(e.target.value === 'dark')}
                className="w-full py-2.5 px-3 rounded-lg text-sm"
                style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setStyle('classic')}
                  className="py-2 rounded-lg text-[13px] font-medium cursor-pointer"
                  style={{
                    border: style === 'classic' ? '2px solid #635BFF' : `1px solid ${theme.borderColor}`,
                    background: style === 'classic' ? 'rgba(99, 91, 255, 0.1)' : 'transparent',
                    color: style === 'classic' ? '#635BFF' : theme.textSecondary
                  }}
                >
                  Classic
                </button>
                <button
                  onClick={() => setStyle('liquid')}
                  className="py-2 rounded-lg text-[13px] font-medium cursor-pointer"
                  style={{
                    border: style === 'liquid' ? '2px solid #635BFF' : `1px solid ${theme.borderColor}`,
                    background: style === 'liquid' ? 'rgba(99, 91, 255, 0.1)' : 'transparent',
                    color: style === 'liquid' ? '#635BFF' : theme.textSecondary
                  }}
                >
                  Liquid Glass
                </button>
              </div>
            </div>
          </div>

          {/* Custom Time and Date Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                Custom Time
              </label>
              <input
                type="text"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                placeholder="10:12"
                className="w-full py-2.5 px-3 rounded-lg text-sm box-border"
                style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
              />
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                Custom Date
              </label>
              <input
                type="text"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                placeholder="Monday, November 24"
                className="w-full py-2.5 px-3 rounded-lg text-sm box-border"
                style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
              />
            </div>
          </div>
        </div>

        {/* Right Panel - iPhone Preview */}
        <div className="flex-[0_0_420px] flex flex-col gap-4 items-center">
          <h3 className="m-0 text-lg font-semibold self-start">Preview</h3>

          {/* iPhone Frame */}
          <div
            className="rounded-2xl p-4"
            style={{ background: '#f5f5f5', border: `1px solid ${theme.borderColor}` }}
          >
            <div
              ref={previewRef}
              className="w-[390px] h-[844px] rounded-[49px] bg-black overflow-hidden relative"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}
            >
              {/* Wallpaper Background */}
              <div className="absolute inset-0" style={{ background: selectedWallpaper.gradient }} />

              {/* Status Bar */}
              <div className="relative z-20 flex justify-between items-start py-3 px-8 text-white">
                <span className="text-sm font-medium">{carrier}</span>
                <div className="flex gap-2 items-center">
                  <svg width="17" height="11" viewBox="0 0 27 17" fill="#fff">
                    <path d="M22.959 16.9531H25.332C25.9375 16.9531 26.3477 16.5234 26.3477 15.8887V1.06445C26.3477 0.429688 25.9375 0 25.332 0H22.959C22.3535 0 21.9336 0.429688 21.9336 1.06445V15.8887C21.9336 16.5234 22.3535 16.9531 22.959 16.9531Z" />
                    <path d="M15.6543 16.9531H18.0078C18.6133 16.9531 19.0332 16.5234 19.0332 15.8887V4.92188C19.0332 4.28711 18.6133 3.85742 18.0078 3.85742H15.6543C15.0391 3.85742 14.6289 4.28711 14.6289 4.92188V15.8887C14.6289 16.5234 15.0391 16.9531 15.6543 16.9531Z" />
                    <path d="M8.33984 16.9531H10.6934C11.3086 16.9531 11.7188 16.5234 11.7188 15.8887V8.45703C11.7188 7.82227 11.3086 7.39258 10.6934 7.39258H8.33984C7.72461 7.39258 7.31445 7.82227 7.31445 8.45703V15.8887C7.31445 16.5234 7.72461 16.9531 8.33984 16.9531Z" />
                    <path d="M1.02539 16.9531H3.37891C3.99414 16.9531 4.4043 16.5234 4.4043 15.8887V11.5039C4.4043 10.8691 3.99414 10.4492 3.37891 10.4492H1.02539C0.410156 10.4492 0 10.8691 0 11.5039V15.8887C0 16.5234 0.410156 16.9531 1.02539 16.9531Z" />
                  </svg>
                  <svg width="15" height="11" viewBox="0 0 23 17" fill="#fff">
                    <path d="M1.4761 6.84972C1.67141 7.03538 1.94485 7.03538 2.13039 6.83996C4.53274 4.28963 7.6968 2.94117 11.2417 2.94117C14.8062 2.94117 17.9898 4.2994 20.3726 6.84972C20.5484 7.02561 20.812 7.01584 21.0073 6.83018L22.355 5.48174C22.5308 5.30584 22.521 5.09088 22.3843 4.92477C20.0894 2.09106 15.773 0.00976562 11.2417 0.00976562C6.72024 0.00976562 2.3843 2.09106 0.0991432 4.92477C-0.0375756 5.09088 -0.0375756 5.30584 0.12844 5.48174L1.4761 6.84972Z" />
                    <path d="M5.52878 10.9341C5.74362 11.1393 6.0073 11.11 6.20261 10.8951C7.37448 9.59548 9.28855 8.64766 11.2416 8.65743C13.2143 8.64766 15.1283 9.6248 16.3197 10.9244C16.4955 11.1295 16.7397 11.1198 16.9545 10.9244L18.4682 9.4196C18.6244 9.26326 18.644 9.04829 18.4975 8.87241C17.0229 7.0647 14.2885 5.70648 11.2416 5.70648C8.1948 5.70648 5.46042 7.0647 3.98581 8.87241C3.83933 9.04829 3.84909 9.24371 4.01511 9.4196L5.52878 10.9341Z" />
                    <path d="M11.2417 16.2498C11.4566 16.2498 11.6421 16.1521 12.023 15.7808L14.4058 13.4942C14.5523 13.3477 14.5913 13.1327 14.4546 12.9568C13.8198 12.136 12.6187 11.4227 11.2417 11.4227C9.82571 11.4227 8.62453 12.1654 7.98977 13.0155C7.89211 13.1719 7.93117 13.3477 8.08742 13.4942L10.4605 15.7808C10.8413 16.1423 11.0269 16.2498 11.2417 16.2498Z" />
                  </svg>
                  <svg width="25" height="11" viewBox="0 0 29 13" fill="#fff">
                    <path d="M5.02041 12.6861H20.1843C21.9541 12.6861 23.2181 12.4875 24.1203 11.5853C25.0256 10.683 25.2114 9.4416 25.2114 7.66573V5.03555C25.2114 3.25967 25.0256 2.01221 24.1203 1.11299C23.215 0.210742 21.9541 0.0151367 20.1843 0.0151367H4.96455C3.26338 0.0151367 1.99639 0.213769 1.09414 1.11904C0.188867 2.02129 0 3.27246 0 4.97295V7.66573C0 9.4416 0.18584 10.686 1.08809 11.5853C1.99639 12.4875 3.2543 12.6861 5.02041 12.6861ZM4.76279 11.4771C3.61162 11.4771 2.5751 11.295 1.97334 10.7C1.38135 10.0982 1.20899 9.07754 1.20899 7.92334V4.83076C1.20899 3.62979 1.38135 2.59697 1.97031 1.99522C2.57207 1.39043 3.62442 1.2211 4.82236 1.2211H20.4486C21.5998 1.2211 22.6363 1.40625 23.2283 1.99824C23.8301 2.6 24.0024 3.61397 24.0024 4.76817V7.92334C24.0024 9.07754 23.8271 10.0982 23.2283 10.7C22.6363 11.298 21.5998 11.4771 20.4486 11.4771H4.76279ZM26.3428 8.72823C27.0658 8.68243 28.0435 7.75059 28.0435 6.34727C28.0435 4.94766 27.0658 4.01582 26.3428 3.97002V8.72823Z" opacity="0.25" />
                    <path d="M4.4208 10.4745H15.5968C16.4825 10.4745 17.0033 10.3375 17.3313 10.0065C17.6652 9.67256 17.8022 9.14873 17.8022 8.27207V4.42617C17.8022 3.54346 17.6652 3.02569 17.3373 2.6917C17.0033 2.36377 16.4667 2.22373 15.5968 2.22373H4.48037C3.53809 2.22373 2.99844 2.36075 2.68028 2.68867C2.35235 3.02266 2.21534 3.56231 2.21534 4.48203V8.27207C2.21534 9.15781 2.35235 9.67256 2.68028 10.0065C3.01426 10.3345 3.55088 10.4745 4.4208 10.4745Z" />
                  </svg>
                </div>
              </div>

              {/* Date and Time Display */}
              <div className="relative z-20 mt-20 text-center text-white">
                <h2 className="text-xl font-normal m-0">{customDate}</h2>
                <div
                  className="text-[85px] font-bold leading-none"
                  style={{
                    letterSpacing: '-0.01em',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0.5), rgba(255,255,255,0.2))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: '0 2px 6px rgba(0,0,0,0.15)'
                  }}
                >
                  {customTime.replace(':', '∶')}
                </div>
              </div>

              {/* Notification */}
              <div
                className="absolute left-1/2 -translate-x-1/2 w-[95%] z-20"
                style={getPositionStyles()}
              >
                {style === 'liquid' ? (
                  <div className="relative overflow-hidden rounded-3xl isolate">
                    <div
                      className="absolute inset-0 rounded-3xl"
                      style={{ backdropFilter: 'blur(8px) saturate(150%)', WebkitBackdropFilter: 'blur(8px) saturate(150%)' }}
                    />
                    <div
                      className="absolute inset-0 rounded-3xl pointer-events-none"
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        boxShadow: 'inset 0 0 28px -10px rgba(255,255,255,0.6), 0 0 0 1px rgba(255,255,255,0.35)'
                      }}
                    />
                    <div
                      className="absolute inset-0 rounded-3xl pointer-events-none"
                      style={{ border: '1px solid rgba(255,255,255,0.5)' }}
                    />

                    <div className="relative z-30 p-3 px-4 flex gap-3 items-start">
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center overflow-hidden self-center flex-shrink-0"
                        style={{
                          background: 'linear-gradient(to bottom right, rgba(255,255,255,0.6), rgba(255,255,255,0.3))',
                          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.4)'
                        }}
                      >
                        {customAppIcon ? (
                          <img src={customAppIcon} alt="app" className="w-full h-full object-cover" />
                        ) : (
                          <Icon icon={selectedApp.icon} className="text-[32px]" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <h4
                            className="m-0 text-[15px] font-semibold"
                            style={{ color: notificationTheme.text, textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                          >
                            {title}
                          </h4>
                          <span className="text-[13px]" style={{ color: notificationTheme.textSecondary }}>
                            {timeOptions.find(t => t.value === notificationTime)?.label || notificationTime}
                          </span>
                        </div>
                        <p className="m-0 mt-0.5 text-[15px] leading-snug" style={{ color: notificationTheme.text }}>
                          {description}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="rounded-[20px] p-3 px-4 flex gap-3 items-start shadow-lg"
                    style={{
                      background: isDarkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)'
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center overflow-hidden self-center flex-shrink-0">
                      {customAppIcon ? (
                        <img src={customAppIcon} alt="app" className="w-full h-full object-cover" />
                      ) : (
                        <Icon icon={selectedApp.icon} className="text-[32px]" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <h4 className="m-0 text-[15px] font-semibold" style={{ color: notificationTheme.text }}>
                          {title}
                        </h4>
                        <span className="text-[13px]" style={{ color: notificationTheme.textSecondary }}>
                          {timeOptions.find(t => t.value === notificationTime)?.label || notificationTime}
                        </span>
                      </div>
                      <p className="m-0 mt-0.5 text-[15px] leading-snug" style={{ color: notificationTheme.text }}>
                        {description}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Home Indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[134px] h-[5px] bg-white rounded-full" />
            </div>
          </div>

          {/* Download Button */}
          <LoadingButton
            onClick={exportAsImage}
            loading={isExporting}
            loadingText="Exporting..."
            icon="mdi:download"
            variant="primary"
            size="lg"
            className="w-full"
          >
            Download Image
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

export default FakeNotificationGenerator;
